import React, { useEffect } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import { useNavigation, useRoute } from "@react-navigation/native"
import { ActivityIndicator } from "react-native-paper"
import * as amplitude from "@amplitude/analytics-react-native"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { brevetFunnelService } from "./services/brevetFunnelService"
import { useBrevetFunnel } from "./context/BrevetFunnelContext"

/**
 * Admin-issued magic link landing: GET /brevet/resume?user=<base64email>.
 *
 * Calls the resume endpoint (behind `auth_or_email`), hydrates the funnel
 * Context with the persisted FastTrackState, then navigation.replace's to
 * the screen matching the current state.
 *
 * No token, no expiration — the admin who shares the link already has BO
 * access; the URL is short-lived in practice (sent in a support call).
 */
// Map each state to the full stack history so navigation.goBack() walks back
// through the previous funnel steps natively. Avoids relying on canGoBack()
// quirks across navigator contexts (magic link entries had no usable stack).
// `null` covers two cases (backend 1:N):
//   1) user has no FastTrackState at all (orphan / fresh student)
//   2) user has only non-brevet rows (trial_done, checkout_completed, …)
// Both land on brevet/start with hydrated user info from /resume.
const STATE_TO_STACK = {
    brevet_started: ["brevet/start"],
    brevet_diag_completed: ["brevet/start", "brevet/diag-2", "brevet/reco"],
    brevet_checkout_created: ["brevet/start", "brevet/diag-2", "brevet/reco", "brevet/pricing-checkout"],
    brevet_paid: ["brevet/confirmation"],
    null: ["brevet/start"],
}

const mapStateCheckoutParamsToContext = (state, checkoutParams, user) => {
    const cp = checkoutParams || {}
    return {
        funnelStateId: state.funnel_state_id || null,
        studentFirstname:
            cp.firstname || user?.firstname || "",
        studentClass: cp.student_class || user?.student_class || null,
        parentEmail: user?.email || "",
        referralCode: cp.referral_code || null,
        subjects: cp.subjects || [],
        selectedModules: cp.selected_modules || [],
        objective: cp.objective || null,
        brevetBlanc: cp.brevet_blanc || null,
        niveauActuel: cp.niveau_actuel || null,
        autonomie: cp.autonomie || null,
        besoinsSpeciaux: cp.besoins_speciaux || [],
        interets: cp.interets || "",
        timePerWeekend: cp.time_per_weekend || null,
        selectedFormulaId: cp.formula_id || null,
        selectedSlotId: cp.slot_id || null,
        paid: state.state === "brevet_paid",
    }
}

const BrevetResumeScreen = (props) => {
    const navigation = useNavigation()
    const route = useRoute()
    const { update, reset } = useBrevetFunnel()

    const userParam =
        route?.params?.user ??
        (typeof window !== "undefined"
            ? new URLSearchParams(window.location?.search || "").get("user")
            : null)

    useEffect(() => {
        if (!userParam) return
        // Reset first so the Context doesn't keep stale values from a previous lead
        // sharing the same browser tab (admin-on-laptop flow).
        reset()
        props.apiRequest(brevetFunnelService.resume, {}, {}, { user: userParam })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userParam])

    useApiStatus(
        brevetFunnelService.resume,
        null,
        false,
        (data) => {
            if (!data) return
            const stateName = data.state
            update(
                mapStateCheckoutParamsToContext(
                    data,
                    data.checkout_params,
                    data.user
                )
            )

            // Track the magic-link "fresh start" branch (state=null = user has
            // no brevet row yet). Lets us measure how many admin links land on
            // an empty funnel vs a resumed one.
            if (stateName === null) {
                amplitude.track("brevet_resume_fresh_start", {
                    has_user: !!data.user?.email,
                    has_student_class: !!data.user?.student_class,
                })
            }

            const stackKey = stateName === null ? "null" : stateName
            const stack = STATE_TO_STACK[stackKey] || ["brevet/start"]
            navigation.reset({
                index: stack.length - 1,
                routes: stack.map((name) => ({ name })),
            })
        }
    )

    if (!userParam) {
        return (
            <View style={styles.container}>
                <TextM
                    color={theme.colors.error}
                    wrap
                    style={styles.errorText}
                    fontWeight="Bold"
                    fontSize={14}
                >
                    Lien invalide ou expiré
                </TextM>
                <FilledButtonM
                    onPress={() => navigation.replace("brevet/start")}
                    label="Reprendre depuis le début"
                />
            </View>
        )
    }

    return (
        <View style={styles.container}>
            {props.error ? (
                <>
                    <TextM
                        color={theme.colors.error}
                        wrap
                        style={styles.errorText}
                        fontWeight="Bold"
                        fontSize={14}
                    >
                        Lien invalide ou expiré
                    </TextM>
                    <FilledButtonM
                        onPress={() => navigation.replace("brevet/start")}
                        label="Reprendre depuis le début"
                    />
                </>
            ) : (
                <>
                    <ActivityIndicator
                        color={theme.colors.primary}
                        style={{ marginTop: 30 }}
                    />
                    <TextM wrap>Reprise du parcours en cours…</TextM>
                </>
            )}
        </View>
    )
}

const styles = {
    container: {
        alignItems: "center",
        flex: 1,
        padding: 20,
    },
    errorText: {
        marginTop: 20,
        marginBottom: 20,
        textAlign: "center",
    },
}

const mapStateToProps = (state) => ({
    request: state.api?.resume?.request,
    success: state.api?.resume?.success,
    error: state.api?.resume?.error,
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(BrevetResumeScreen)
